import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { createFirebaseOrder, hasUserPendingOrderForApp } from "@/lib/firebaseOrders";
import { isFirebaseConfigured } from "@/lib/firebase";
import { getCatalogApp, isAppAvailable } from "@/lib/catalog";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import RecaptchaBox, { isRecaptchaEnabled } from "@/components/RecaptchaBox";
import { Upload, ShieldCheck, Copy, Check, Box, MailCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  checkCheckoutRateLimit, recordCheckoutAttempt, clearCheckoutRateLimit,
  checkUploadRateLimit, recordUploadAttempt, formatRemainingTime,
} from "@/lib/rateLimit";
import {
  hasPendingOrder, recordPendingOrder, logSecurityEvent,
} from "@/lib/security";
import {
  PAYMENT_METHODS,
  PAYMENT_METHOD_KEYS,
  DEFAULT_PAYMENT_METHOD,
  PAYMENT_INSTRUCTIONS,
} from "@/lib/payments";

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024; // 10 MB
const COPY_RESET_MS = 1500;
const LOCAL_ORDERS_KEY = "weborder.localOrders";

function getCheckoutErrorMessage(error) {
  const detail = error?.response?.data?.detail || error?.message || "";
  if (/captcha/i.test(detail)) {
    return detail;
  }
  if (/permission|insufficient/i.test(detail)) {
    return "Order တင်ရန် permission မရပါ။ Login ပြန်ဝင်ပြီး ထပ်စမ်းကြည့်ပါ။";
  }
  if (/network|timeout|offline/i.test(detail)) {
    return "Network connection မကောင်းပါ။ ခဏနေပြီး ထပ်စမ်းကြည့်ပါ။";
  }
  return detail || "အော်ဒါတင်ခြင်း မအောင်မြင်ပါ။ ခဏနေပြီး ထပ်စမ်းကြည့်ပါ။";
}

function getLocalOrders() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_ORDERS_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveLocalOrder(order) {
  const existing = getLocalOrders().filter((item) => item.order_id !== order.order_id);
  localStorage.setItem(LOCAL_ORDERS_KEY, JSON.stringify([order, ...existing]));
}

async function buildLocalOrder({ orderId, app, appId, user, paymentMethod, screenshot }) {
  return {
    order_id: orderId,
    app_id: appId,
    app_title: app.title,
    user_id: user?.user_id || "local_guest",
    user_email: user?.email || "",
    user_name: user?.name || "Local User",
    payment_method: paymentMethod,
    amount_mmk: app.price_mmk,
    screenshot_data_url: await readFileAsDataUrl(screenshot),
    screenshot_path: null,
    status: "pending",
    created_at: new Date().toISOString(),
  };
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Checkout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, sendVerificationEmail, reloadUser } = useAuth();
  const [app, setApp] = useState(null);
  const [method, setMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [sendingVerification, setSendingVerification] = useState(false);
  const [rateLimited, setRateLimited] = useState(null);

  const emailVerified = user?.emailVerified ?? true; // default true for Google-auth users

  useEffect(() => {
    const fallbackApp = getCatalogApp(id);
    if (fallbackApp) {
      setApp(fallbackApp);
    }

    if (!fallbackApp || !isAppAvailable(fallbackApp)) {
      toast.info("ဤဗားရှင်း မကြာမီ ရနိုင်ပါမည်။");
      navigate("/");
      return;
    }
  }, [id, navigate]);

  const onFile = (f) => {
    if (!f) return;
    // Rate limit screenshot uploads
    const uploadLimit = checkUploadRateLimit();
    if (!uploadLimit.allowed) {
      toast.error(`Upload လုပ်ရန် ခဏစောင့်ပါ (${formatRemainingTime(uploadLimit.remainingMs)})`);
      return;
    }
    if (!f.type.startsWith("image/")) { toast.error("ပုံဖိုင်သာ ထည့်ပါ"); return; }
    if (f.size > MAX_SCREENSHOT_BYTES) { toast.error("ပုံ အရွယ်အစား ၁၀MB ထက် မပိုရပါ"); return; }
    recordUploadAttempt();
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleSendVerification = async () => {
    setSendingVerification(true);
    try {
      await sendVerificationEmail();
      toast.success("အတည်ပြု email ကို ပို့ပြီးပါပြီ။ Inbox စစ်ဆေးပါ။");
    } catch (err) {
      toast.error(err?.message || "Email ပို့ခြင်း မအောင်မြင်ပါ");
    } finally {
      setSendingVerification(false);
    }
  };

  const handleReloadUser = async () => {
    try {
      await reloadUser();
      toast.success("Account status ပြန်လည်စစ်ဆေးပြီးပါပြီ");
    } catch {
      toast.error("Account ပြန်စစ်ဆေး၍ မရပါ");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("ငွေပေးချေမှု Screenshot ထည့်ပါ"); return; }

    // Email verification check
    if (!emailVerified) {
      toast.error("Email အတည်မပြုရသေးပါ။ ကျေးဇူးပြု၍ email ကို အတည်ပြုပါ။");
      return;
    }

    // Rate limit check
    const checkoutLimit = checkCheckoutRateLimit();
    if (!checkoutLimit.allowed) {
      setRateLimited(checkoutLimit.remainingMs);
      toast.error(`Order တင်ရန် ခဏစောင့်ပါ (${formatRemainingTime(checkoutLimit.remainingMs)})`);
      logSecurityEvent("checkout_rate_limited", { userId: user?.user_id, appId: id });
      return;
    }

    // Duplicate pending order check
    if (hasPendingOrder(user?.user_id, id)) {
      toast.error("ဤ App အတွက် ယခုစောင့်ဆိုင်းနေသော အော်ဒါ ရှိပြီးသားဖြစ်ပါသည်။ Admin စစ်ဆေးပေးပါမည်။");
      return;
    }

    if (isRecaptchaEnabled() && !captchaToken) {
      toast.error("Please complete the CAPTCHA first.");
      return;
    }

    setSubmitting(true);
    recordCheckoutAttempt();

    try {
      if (isFirebaseConfigured) {
        // Check for duplicate pending orders on Firestore
        const hasExisting = await hasUserPendingOrderForApp(user.user_id, id);
        if (hasExisting) {
          toast.error("ဤ App အတွက် စောင့်ဆိုင်းနေသော အော်ဒါ ရှိပြီးသားဖြစ်ပါသည်။");
          return;
        }

        const orderId = await createFirebaseOrder({ app, user, paymentMethod: method, screenshot: file });
        recordPendingOrder(user.user_id, id, orderId);
        clearCheckoutRateLimit(); // Reset rate limit on success

        saveLocalOrder(await buildLocalOrder({
          orderId,
          app,
          appId: id,
          user,
          paymentMethod: method,
          screenshot: file,
        }));
        toast.success("အော်ဒါတင်ပြီးပါပြီ! Admin က စစ်ဆေးပေးပါမည်");
        navigate("/dashboard");
        return;
      }
      throw new Error("Firebase is not configured");
    } catch (e) {
      console.error("Checkout submission failed", e);
      logSecurityEvent("checkout_failed", { userId: user?.user_id, appId: id, error: e?.message });
      if (!app) {
        toast.error(getCheckoutErrorMessage(e));
        return;
      }
      if (isFirebaseConfigured) {
        toast.error(getCheckoutErrorMessage(e));
        return;
      }
      saveLocalOrder(await buildLocalOrder({
        orderId: `local_order_${Date.now()}`,
        app,
        appId: id,
        user,
        paymentMethod: method,
        screenshot: file,
      }));
      toast.success("Local preview order တင်ပြီးပါပြီ");
      navigate("/dashboard");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), COPY_RESET_MS);
  };

  if (!app) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">ဖွင့်နေသည်…</div>;

  const selectedPay = PAYMENT_METHODS[method];

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="text-sm tracking-wide text-zinc-500 font-medium">ငွေပေးချေမှု</div>
      <h1 className="text-4xl font-bold tracking-tight">ဝယ်ယူမှု အပြီးသတ်ပါ</h1>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: payment instructions */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h2 className="font-semibold text-xl">၁။ ငွေပေးချေနည်း ရွေးချယ်ပါ</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              {PAYMENT_METHOD_KEYS.map((m) => {
                const info = PAYMENT_METHODS[m];
                return (
                  <button
                    key={m}
                    data-testid={`method-${m}`}
                    onClick={() => setMethod(m)}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      method === m ? "border-[#7CC576] bg-blue-50/40" : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <div className="font-semibold">{info.label}</div>
                    <div className="text-xs text-zinc-500 mt-1">{info.branch}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h2 className="font-semibold text-xl">၂။ {app.price_mmk.toLocaleString()} ကျပ် လွှဲပါ</h2>
            <div className="mt-4 space-y-3">
              <DetailRow label="ဘဏ်" value={selectedPay.bank} />
              <DetailRow label="မှူးဆက်သွယ်ရာ ဘဏ်ခွဲ" value={selectedPay.branch} />
              <DetailRow label="အကောင့်အမည်" value={selectedPay.account_name} onCopy={() => copy(selectedPay.account_name, "an")} copied={copied === "an"} />
              <DetailRow label="အကောင့်နံပါတ်" value={selectedPay.account_number} onCopy={() => copy(selectedPay.account_number, "anum")} copied={copied === "anum"} />
              <DetailRow label="ပမာဏ (ကျပ်)" value={app.price_mmk.toLocaleString()} onCopy={() => copy(String(app.price_mmk), "amt")} copied={copied === "amt"} />
            </div>
            <div className="mt-4 text-xs text-zinc-500 flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              <ShieldCheck className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <span>{PAYMENT_INSTRUCTIONS}</span>
            </div>
          </div>

          <div className="bg-white border border-zinc-200 rounded-2xl p-6">
            <h2 className="font-semibold text-xl">၃။ ငွေလွှဲ Screenshot ထည့်ပါ</h2>
            <label
              data-testid="screenshot-dropzone"
              className="mt-4 block border-2 border-dashed border-zinc-300 bg-zinc-50 rounded-xl p-8 hover:border-[#7CC576] hover:bg-blue-50/50 cursor-pointer transition-colors"
            >
              {preview ? (
                <img src={preview} alt="preview" className="max-h-64 mx-auto rounded-lg" />
              ) : (
                <div className="text-center">
                  <Upload className="w-10 h-10 mx-auto text-zinc-400" />
                  <div className="mt-3 font-medium text-zinc-700">Screenshot ပုံချ သို့မဟုတ် click နှိပ်ပြီး ထည့်ပါ</div>
                  <div className="text-xs text-zinc-500 mt-1">PNG / JPG · ၁၀MB အထိ</div>
                </div>
              )}
              <input data-testid="screenshot-input" type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        {/* Right: order summary */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-zinc-200 rounded-2xl p-6 sticky top-24">
            <h3 className="font-semibold text-lg">အော်ဒါအနှစ်ချုပ်</h3>
            <div className="mt-4 flex items-center gap-3 pb-4 border-b border-zinc-100">
              <div className="w-14 h-14 rounded-xl overflow-hidden border border-zinc-200">
                {app.icon_url && !app.title?.includes("MyanTax Box") ? (
                  <img src={fileUrl(app.icon_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <MyanTaxLogo />
                )}
              </div>
              <div className="min-w-0">
                <div className="font-medium truncate">{app.title}</div>
                <div className="text-xs text-zinc-500">{app.developer} · v{app.version}</div>
              </div>
            </div>
            <div className="space-y-2 mt-4 text-sm">
              <Row label="စျေးနှုန်း" value={`${app.price_mmk.toLocaleString()} ကျပ်`} />
              <Row label="ဝန်ဆောင်ခ" value="၀ ကျပ်" />
              <Row label="စုစုပေါင်း" value={`${app.price_mmk.toLocaleString()} ကျပ်`} bold />
            </div>
            {/* Email Verification Warning */}
            {!emailVerified && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <MailCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <div className="font-semibold">Email အတည်ပြုရန် လိုအပ်ပါသည်</div>
                    <div className="text-xs mt-1">Order တင်ရန် အီးမေးလ်ကို အတည်ပြုပါ။ Inbox (နှင့် Spam folder) စစ်ဆေးပါ။</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleSendVerification}
                    disabled={sendingVerification}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-xs rounded-lg"
                  >
                    {sendingVerification ? "ပို့နေသည်…" : "အတည်ပြု Email ပို့ရန်"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleReloadUser}
                    className="text-xs rounded-lg border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    အတည်ပြုပြီးလား စစ်ဆေးရန်
                  </Button>
                </div>
              </div>
            )}

            {/* Rate Limit Warning */}
            {rateLimited && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-700">
                  အော်ဒါတင်ခြင်း ကန့်သတ်ထားသည်။ {formatRemainingTime(rateLimited)} ခဏစောင့်ပြီး ထပ်စမ်းပါ။
                </div>
              </div>
            )}

            <div className="mt-5">
              <RecaptchaBox onTokenChange={setCaptchaToken} />
            </div>
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs leading-5 text-emerald-800">
              Premium access သည် သင့် website account email နှင့် ချိတ်ဆက်ပါမည်။ App ထဲတွင်လည်း email တူတူဖြင့် login ဝင်ပါ။
            </div>
            <Button data-testid="submit-order-btn" onClick={submit} disabled={submitting || !file || !emailVerified} className="w-full mt-6 h-12 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg text-base disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? "တင်နေသည်…" : !emailVerified ? "Email အတည်ပြုရန် လိုအပ်သည်" : "အတည်ပြုရန် တင်သွင်းမည်"}
            </Button>
            <div className="text-xs text-zinc-500 text-center mt-3">Admin က ၂၄ နာရီအတွင်း စစ်ဆေးပေးပါမည်</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MyanTaxLogo() {
  return (
    <div className="w-full h-full bg-[#0F4E2A] text-[#B5DC57] grid place-items-center">
      <Box className="w-7 h-7" />
    </div>
  );
}

function DetailRow({ label, value, onCopy, copied }) {
  return (
    <div className="flex items-center justify-between gap-3 bg-zinc-50 rounded-lg px-4 py-3 border border-zinc-200">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium">{label}</div>
        <div className="font-medium tabular-nums break-all">{value}</div>
      </div>
      {onCopy && (
        <button onClick={onCopy} className="flex-shrink-0 text-zinc-500 hover:text-[#7CC576]">
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={bold ? "font-display font-bold tabular-nums" : "tabular-nums"}>{value}</span>
    </div>
  );
}